import { useTheme, logoImage } from '@quickbit/qb-design-system';
import { jsPDF } from 'jspdf';
import { useCallback, useState } from 'react';
import { SettlementReportWithAccouting } from 'tabs/Accounting/types';
import { getMerchant } from 'tabs/api';
import { KybRead } from 'tabs/Merchants/types';

export interface Args {
  settlementData: SettlementReportWithAccouting;
  merchantKyb: KybRead;
  isPreview?: boolean; // When we mark as processed and send the email, we don't want to show the preview watermark
}

export const useGeneratePDF = () => {
  const { palette } = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const generatePDF = useCallback(
    async ({ settlementData, merchantKyb, isPreview = true }: Args) => {
      try {
        setLoading(true);

        const {
          settlementAmount,
          processingFee,
          gatewayFee,
          settlementFee,
          totalAmount,
          adjustments,
          refundDebt,
          merchant: { name: merchantName, uuid },
          startTime,
          endTime,
        } = settlementData;

        const {
          registeredAddress: { postalNumber, postalRegion, country, address1 },
        } = merchantKyb;
        const merchantAddress = [address1, postalRegion, postalNumber, country];

        const merchant = await getMerchant(uuid);

        //PDF generation starts here
        const pdf = new jsPDF();

        const processingFeePercentage = totalAmount
          ? (processingFee / totalAmount) * 100
          : 0;

        if (isPreview && !settlementData.processed) {
          pdf.setFontSize(100);
          pdf.setTextColor(230, 230, 230); //very light grey
          pdf.text('PREVIEW', 140, 210, {
            align: 'center',
            angle: 45,
            baseline: 'middle',
          });
          pdf.setTextColor(0, 0, 0);
        }

        pdf.addImage({
          imageData: logoImage,
          x: 150,
          y: 20,
          width: 50,
          height: 12,
          format: 'WEBP',
          compression: 'FAST',
        });
        pdf.setFontSize(22);
        pdf.text('Settlement Report', 10, 19);
        pdf.setFontSize(10);
        pdf.text(
          `Date: ${new Date().toLocaleString('default', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}`,
          10,
          25
        );

        pdf.setFontSize(10);
        pdf.setFont(pdf.getFont().fontName, 'bold');
        pdf.text('Period', 10, 45);
        pdf.setFont(pdf.getFont().fontName, 'normal');
        pdf.text(startTime.toLocaleString(), 10, 50);
        pdf.text(endTime.toLocaleString(), 10, 55);

        //Merchant details heading
        pdf.setFont(pdf.getFont().fontName, 'bold');
        pdf.text('Merchant', 10, 70);
        pdf.text('Remittance details', 150, 70, { align: 'left' });

        //Merchant details
        pdf.setFont(pdf.getFont().fontName, 'normal');
        pdf.text([merchantName, ...merchantAddress], 10, 75);
        pdf.setFont(pdf.getFont().fontName, 'bold');
        pdf.text('IBAN:', 150, 75, { align: 'left' });
        pdf.text('BIC:', 150, 80, { align: 'left' });
        pdf.setFont(pdf.getFont().fontName, 'normal');
        pdf.text(merchant.paymentDetails?.iban || '', 161, 75, {
          align: 'left',
        });
        pdf.text(merchant.paymentDetails?.bic || '', 158, 80, {
          align: 'left',
        });

        //Items & Amount heading
        pdf.line(10, 100, 200, 100);
        pdf.setFont(pdf.getFont().fontName, 'bold');
        pdf.text('Item', 10, 105);
        pdf.text('Amount (EUR)', 200, 105, { align: 'right' });
        pdf.line(10, 108, 200, 108);

        //Items & Amount
        pdf.setFont(pdf.getFont().fontName, 'normal');

        pdf.text('Gross volume processed', 10, 115);
        pdf.text(totalAmount.toString(), 200, 115, { align: 'right' });

        pdf.text(
          `Processing fee (${processingFeePercentage.toFixed(2)}%)`,
          10,
          120
        );
        pdf.text((-processingFee).toString(), 200, 120, { align: 'right' });

        pdf.text(`Gateway fee`, 10, 125);
        pdf.text((-gatewayFee).toString(), 200, 125, { align: 'right' });

        pdf.text(`Settlement fee`, 10, 130);
        pdf.text((-settlementFee).toString(), 200, 130, { align: 'right' });

        pdf.text('Subtotal', 10, 135);
        pdf.text(settlementAmount.toString(), 200, 135, { align: 'right' });

        if (refundDebt) {
          pdf.text('Refund debt adjustment', 10, 140);
          pdf.text(refundDebt.toString(), 200, 140, {
            align: 'right',
          });
        }
        let startingYCoordinate = 145;
        let adjustmentsTotal = 0;
        adjustments.forEach(({ amount, label }) => {
          pdf.text(label, 10, startingYCoordinate);
          pdf.text(amount.toString(), 200, startingYCoordinate, {
            align: 'right',
          });
          startingYCoordinate += 5;
          adjustmentsTotal += amount;
        });

        //Total section
        const grandTotal =
          settlementAmount + adjustmentsTotal + (refundDebt || 0);
        pdf.line(10, 180, 200, 180);

        pdf.setFontSize(14);
        pdf.text('Net settlement', 10, 185);
        pdf.text(grandTotal.toFixed(2), 200, 185, {
          align: 'right',
        });

        pdf.text('Tax', 10, 192);
        pdf.text('0%', 200, 192, {
          align: 'right',
        });

        pdf.text('Total', 10, 199);
        pdf.text(grandTotal.toFixed(2), 200, 199, {
          align: 'right',
        });

        pdf.line(10, 202, 200, 202);

        //Footer
        pdf.setFontSize(10);
        pdf.line(10, 270, 200, 270);
        pdf.setTextColor(palette.grey.x500);
        pdf.setFont(pdf.getFont().fontName, 'italic');
        pdf.text(
          ['QB Europe AB', 'Sveavägen 31', '111 34 Stockholm', 'Sweden'],
          10,
          275
        );
        pdf.text(
          [
            'Org. nr: 559265-3793',
            'VAT nr.: SE559265379301',
            'accounting@quickbit.com ',
          ],
          150,
          275
        );

        setLoading(false);
        return pdf;
      } catch (e) {
        console.error(e);
        setLoading(false);
        setError(true);
      }
    },
    [palette.grey.x500]
  );

  return { generatePDF, loading, error, setError };
};
